import * as manyIcon from 'icons/multiple-users-1.svg'
import { all, Button, Div, H2, Hr, Label, Number as NumberInput, Span, Canvas, P, reactive } from 'alkali'
import { sortBy } from 'lodash'

import { evidenceStats } from 'model/EvidenceSearch'
import { demographics, faqOrReadOnly, currentAnalysisSelections, structuredQuery } from 'model/SearchQuery'
import { studyStats, flagFiltered, selectedStudyData } from 'model/StudyCollection'

import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import { Toast, getToastPosition, removeToast } from 'components/generic/Toast'
import { showRegressionForCovariate } from 'components/analysis/RegressionDialog'
import * as arrowDown from 'icons/selectivity-caret.svg'

import * as booksIcon from 'icons/notes-paper-text.svg'
import * as manyIcon from 'icons/multiple-users-1.svg'
import * as clockIcon from 'icons/notes-clock.svg'
import * as birthdayIcon from 'icons/cake-birthday.svg'
import * as filterIcon from 'icons/filter.svg'
import * as flagIcon from 'icons/flag-plain-3.svg'
import * as genderIcon from 'icons/multiple-man-woman-4.svg'
import * as raceIcon from 'icons/race.svg'
import * as populationIcon from 'icons/groups_icon.svg'
import * as regressionIcon from 'icons/performance-increase.svg'
import * as controlGraphIcon from 'icons/science-graph.svg'
import * as studyUpload from 'icons/common-file-text-upload.svg'
import * as arrowDown from 'icons/arrow-down-1.svg'
import * as sortIcon from 'icons/data-transfer-vertical.svg'
import Icon from 'components/Icon'
import * as female from 'icons/gender-female.svg'
import * as male from 'icons/gender-male.svg'

import './Demographics.scss'

const selectedDemographics = selectedStudyData.to((selectedStudyData) => {
	let female = 0
	let male = 0
	let ages = []
	let ageTotal = 0
	let ageSDTotal = 0
	let ageN = 0
	let totalN = 0
	let followUpTotal = 0
	let followUpN = 0
	let races = {}
	let lastStudy
	let studiesWithAge = new Set()
	let studiesWithRace = new Set()
	let studiesWithGender = new Set()
	for (let { groups, cells, row, study } of selectedStudyData) {
		for (let cell of cells) {
			let group = groups.find((group) => group.Id == cell.group)
			if (!group.analyzeAs) // only count selected groups
				continue

			let N = cell.N || group.N
			if (N) {
				totalN += N
			}
			if (row.time?.days && N) {
				followUpTotal += (row.time.days || 1) * N
				followUpN += N
			}
			if (N && group.male > -1) {
				studiesWithGender.add(study)
				male += (N * group.male) / 100
				female += (N * (100 - group.male)) / 100
			}
			if (N && group.age) {
				studiesWithAge.add(study)
				let sd = Math.max(group.ageSD || 10, 1)
				let mean = group.age
				ageTotal += mean * N
				ageSDTotal += sd * sd * N
				ageN += N
				for (let i = 0; i < 100; i++) {
					// add the age distribution in based on normal distribution formula
					ages[i] =
						(ages[i] || 0) +
						(Math.exp((-(i - mean) * (i - mean)) / (2 * sd * sd)) /
							(sd * Math.sqrt(2 * Math.PI))) *
							N
				}
			}
			if (N) {
				let totalKnown = 0
				if (group.races) {
					for (var race in group.races) {
						if (race != 'unknown') {
							studiesWithRace.add(study)
							totalKnown += group.races[race]
						}
						races[race] = (races[race] || 0) + (group.races[race] / 100) * N
					}
					if (group.races.unknown == undefined)
						races.unknown = (races.unknown || 0) + ((100 - totalKnown) / 100) * N
				}
			}
		}
	}
	let ageMean = ageTotal / ageN
	let ageSD = Math.sqrt(ageSDTotal / ageN)
	if (ages.length == 0) {
		for (let i = 0; i < 100; i++) {
			// add the age distribution in based on normal distribution formula for a general population
			ages[i] = Math.exp((-(i - 50) * (i - 50)) / (2 * 20 * 20)) / (20 * Math.sqrt(2 * Math.PI))
		}
	}
	let followUpMean = followUpTotal / followUpN
	return {
		female,
		male,
		ages,
		ageMean,
		ageSD,
		totalN,
		followUpMean,
		studiesWithAge: studiesWithAge.size,
		studiesWithRace: studiesWithRace.size,
		studiesWithGender: studiesWithGender.size,
		selectedStudies: selectedStudyData.length,
		races,
	}
})

export let followUpDescription = selectedDemographics.to(({ followUpMean }) =>
	followUpMean > 400
		? numToString(followUpMean / 365.26) + ' years'
		: followUpMean > 100
		? numToString(followUpMean / 30.44) + ' months'
		: followUpMean > 30
		? numToString(followUpMean / 7) + ' weeks'
		: followUpMean > 0
		? numToString(followUpMean) + ' days'
		: ''
)

let filteredDescription = evidenceStats.to((evidenceStats) => {
	let text = []
	if (!evidenceStats) return ''
	if (evidenceStats.outcome) {
		text.push(
			evidenceStats.outcome +
				(evidenceStats.outcome == 1 ? ' was' : ' were') +
				' removed by outcome'
		)
	}
	if (evidenceStats.study) {
		text.push(
			evidenceStats.study +
				(evidenceStats.study == 1 ? ' was' : ' were') +
				' removed by study filters'
		)
	}
	if (evidenceStats.analysis) {
		text.push(
			evidenceStats.analysis +
				(evidenceStats.analysis == 1 ? ' was' : ' were') +
				' removed by analysis requirements'
		)
	}
	if (evidenceStats.incomplete) {
		text.push(
			evidenceStats.incomplete +
				(evidenceStats.incomplete == 1 ? ' was' : ' were') +
				' removed due to lack of groups/data'
		)
	}
	if (evidenceStats.query) {
		text.push(
			evidenceStats.query +
				(evidenceStats.query == 1 ? ' was' : ' were') +
				' removed due to PICO filters'
		)
	}
	let fullText = text.join(', ')
	if (evidenceStats.total && fullText) {
		fullText += ' (out of ' + evidenceStats.total + ' extracted studies).'
	}
	return fullText
})

const canRegress = structuredQuery.interventions.to(interventions =>
	currentAnalysisSelections.analysisType.to(analysisType => analysisType == 'direct' || analysisType == 'auto' && interventions.length == 1))


const displayToast = (demographic, event, message) => {
	event.stopPropagation()

	const existingToast: HTMLElement = document.querySelector(demographic)
	if (existingToast === null) {
		const parentLocation = getToastPosition(event.target)
		const followUpToast = demographic == 'follow-up'
		const toastPosition =  {
			x: parentLocation.position.vwPercent,
			y: `calc(${parentLocation.position.vhPercent} + 7.8%)`,
		}
		const followUpPosition =  {
			x: `calc(${parentLocation.position.vwPercent} - 27px)`,
			y: `calc(${parentLocation.position.vhPercent} + 32px)`,
		}
		const newToast = demographicsToast(demographic, toastPosition, message)
		const resultBody = document.querySelector('#main')
		resultBody.prepend(newToast)
	}
}

const demographicsToast = (demographic, toastPosition, message) => new Toast('.active.hover-toast.top-arrow', {
	id: `${demographic}-toast`,
	style: {position: 'absolute', left: toastPosition.x, top: toastPosition.y},
	hover: true,
	title: `${demographic}`,
	message,
})

const genderGraph = async(graphElement, female, male) => {
 const {Chart, registerables} = await import('chart.js')
    Chart.register(...registerables);
	const roundFemale = Math.round(female)
	const roundMale = Math.round(male)
	const graphData = {
		labels: [
		  'Female',
		  'Male',
		],
		datasets: [{
		  label: 'Gender Distribution',
		  data: [roundFemale, roundMale],
		  backgroundColor: ["rgba(60, 153, 220, 0.8)","rgba(28, 63, 151, 0.42)"],
		  hoverOffset: 5,
		}],
		options: {
			responsive: true,
		}
	  };
	const config = {
		type: 'doughnut',
		data: graphData,
	  };
	  return new Chart(graphElement, config)
}
const ageMessage = selectedDemographics.to(({ ageMean, ageSD, studiesWithAge, selectedStudies }) => {
	const emptyStatement = P('No selected studies reported age, select studies that report age to see age distribution.')
	const clickStatement = P('.click-statement', ['Click to select preferred age or to perform a regression analysis on age.'])
	const dataStatement = P([
		'Average age of participants is ', Span(Math.round(ageMean)), ' ± ', Span(Math.round(ageSD)), ' years based on ',
		Span(studiesWithAge), ' out of ', Span(selectedStudies), ' selected studies reporting age.'
	])
	const ageStatement = isFAQDashboard ? [(ageMean) ? dataStatement : emptyStatement] :  [(ageMean) ? dataStatement : emptyStatement, clickStatement]
	return new Div(ageStatement)
})

const genderMessage = selectedDemographics.to(({ male, female, studiesWithGender, selectedStudies }) => {
	const emptyStatement = P('No selected studies reported gender, select studies that report gender to see gender distribution.')
	const clickStatement = P('.click-statement', ['Click to select preferred gender or to perform a regression analysis on gender.'])
	const dataStatement = P([
		Span([`${Math.round(female)}`]), ' female participants, ', Span([`${Math.round(male)}`]), ' male participants, based on ',
		Span(studiesWithGender),' out of ', Span(selectedStudies), ' selected studies reporting gender.'
	])
	const genderStatement = isFAQDashboard ? [(male || female) ? dataStatement : emptyStatement] :  [(male || female) ? dataStatement : emptyStatement, clickStatement]
	return new Div(genderStatement)
})

const raceMessage = selectedDemographics.to(({ races, studiesWithRace, selectedStudies }) => {
	let total = 0
	for (let race in races) {
		total += races[race]
	}
	let raceBars = []
	for (let race in races) {
		let percent = (races[race] / total) * 100
		let title =
			Math.round(percent) +
			'% ' +
			(raceLabels[race] || race.charAt(0).toUpperCase() + race.slice(1))
		raceBars.push(
			new Div('.race-bar.race-' + race, {
				percent,
				width: (percent > 0 ? Math.max(percent, 5) : 0) + '%',
				title,
				content: percent > 50 && race != 'unknown' ? title : '',
			})
		)
	}
	let noStudies = 'No selected studies reported race, select studies that report race to see race distribution'
	let raceStatement = studiesWithRace
		? new Div([P([Span(studiesWithRace + '/' + selectedStudies), ' selected studies reported race.']), P(['Of the reported overall population, there were ', Object.keys(races).map(race => ' ' + Math.round(races[race]).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")+ ' ' + race) + ' participants.'])])
		: new Div([P(noStudies)])
	return raceStatement
})

const followUpMessage = new Div([followUpDescription.to(
	(followUpDescription) => followUpDescription ? P(['The mean follow-up time was ', Span(followUpDescription)]) : P('Please select more studies with follow-up data'))])


const participantsMessage = selectedDemographics.to((selectedDemographics) => {
	if (selectedDemographics.totalN) {
		return selectedDemographics.totalN.toLocaleString() + ' total participants'
	} else {
		return 'No selected studies reported participants total, select studies that report participants to see a participants total'
	}
}
)

export const Demographics = Div('.demographics', [
	Div('.choice-container', [		
		Div(
			'.age-control.truncate',
			{
				classes: {
					'active-control': currentAnalysisSelections.adjustments.to(
								(adjustments) => adjustments?.some(({ predictor }) => predictor == 'age') ||
								currentAnalysisSelections.regression.to(covariate => covariate == 'age' || 
								demographics.age.to(Boolean))),
				},
				onclick() {
					if (!isFAQDashboard){
						let selectedAge = reactive(demographics.age.valueOf())
						showRegressionForCovariate({
							name: 'age', valueFromGroup: (group) => group.age, selected: demographics.age,
							characteristicSetting: [
								Div('.regression-control', [
									Label('.age-label', ['Prefer studies for this age:']),
									NumberInput('.form-control', {
										value: selectedAge,
										min: 0,
										max: 100,
										placeholder: 'Age',
										readOnly: faqOrReadOnly.valueOf(),
									}),
								]),
							],
							onSave() {
								if (demographics.age != selectedAge.valueOf())
									demographics.age.put(selectedAge.valueOf())
							},
							characteristicAnalysis: [
								Div('.covariate-distribution', [
									Div('.age-graph',
										{
											title: 'Distribution of ages of participants',
										},
										selectedDemographics.to(({ ages }) => {
											let maxCount = ages.reduce((ageCount, max) => Math.max(ageCount, max), 0)
											return ages.map((count, age) =>
												Div('.age-area', {
													height: (90 * count) / maxCount + '%',
													title:
														count > 1
															? 'Approximately ' +
															  Math.round(count) +
															  ' of participants were ' +
															  age +
															  ' years old'
															: 'No studies selected with reported ages',
												})
											)
										})
									),
									Div(
										'.age-graph-numbers',
										selectedDemographics.to(({ ageMean, ageSD }) =>
											ageMean ? ['Average age of participants: ' + Math.round(ageMean) + ' ± ' + Math.round(ageSD)] : ['Please select more studies to run this analysis']
									)),
								])
							]
						})
					}
				},
				onmouseenter(event) {
					displayToast('age', event, ageMessage)
				},
				onmouseleave(){
					removeToast('age-toast')
				}
			},
			[
				Div('.age-indicator.truncate', [
					Icon('.age-icon', { icon: birthdayIcon }),
					Icon('.sort-icon', {
						classes: {
							'age-sorted': demographics.age.to(age => age)
						},
						icon: sortIcon,
					}),
					Span('.sort-span', [demographics.age.to(age =>
						age > 0 ? `${age + ' yrs'} ` : null
					)]),
					Icon('.regression-icon', {
						icon: regressionIcon,
						display: currentAnalysisSelections.regression.to(
							covariate => covariate == 'age')
					}),
					Icon('.graph-icon', {
						icon: controlGraphIcon,
						display: currentAnalysisSelections.regression.to(covariate => covariate == 'age')
					}),
				]),
				Div('.age-summary', [
					selectedDemographics.to(({ ageMean, ageSD, studiesWithAge, selectedStudies }) =>
						ageMean && studiesWithAge >= Math.sqrt(selectedStudies - 1) ? [Math.round(ageMean) + ' ± ' + Math.round(ageSD) + ' yrs old'] : ['Age']),
				]),
			]
		),
		Div('.gender-control.truncate',
			{
				classes: {
					'active-control': currentAnalysisSelections.adjustments.to(
								(adjustments) => adjustments?.some(({ predictor }) => predictor == 'gender') ||
								currentAnalysisSelections.regression.to(covariate => covariate == 'gender' || 
								demographics.gender.to(Boolean))),
				},
				onclick() {
					if (!isFAQDashboard){
					let selectedGender = reactive(demographics.gender.valueOf())
					showRegressionForCovariate({ name: 'gender', valueFromGroup: (group) => group.male, selected: demographics.gender,
						characteristicSetting: [
							Div('.regression-control', [
								Label('Prefer studies for this gender:'),
								Div('.gender-indicator', [
									Button([Span('.gender-icon', ['♀ ']), 'Female'], {
										classes: {
											selected: selectedGender.to((gender) => gender == 'female'),
										},
										title: 'Select female patients',
										onclick() {
											if (!faqOrReadOnly.valueOf())
												selectedGender.put(selectedGender == 'female' ? null : 'female')
										},
									}),
									Button(['Male', Span('.gender-icon', [' ♂'])], {
										classes: {
											selected: selectedGender.to((gender) => gender == 'male'),
										},
										title: 'Select male patients',
										onclick() {
											if (!faqOrReadOnly.valueOf())
												selectedGender.put(selectedGender == 'male' ? null : 'male')
										},
									}),
								]),
							]),
						],
						onSave() {
							if (demographics.gender != selectedGender.valueOf())
								demographics.gender.put(selectedGender.valueOf())
						},
						characteristicAnalysis: [
							selectedDemographics.to(({ female, male }) => {
								return (female & male) ? Div(
									'.gender-graph', [Canvas('#genderGraph', {
										attached() {
											genderGraph(this,female, male)
										},
									})]) : Div(
										'.no-data', [P(['Not enough gender data',]), P([Span('.strong', ['Note: ']),'To utilize this analysis, select more studies with analyzable gender data'])])
							})
						]
					})
				}
				},
				onmouseenter(event) {
					displayToast('gender', event, genderMessage)
				},
				onmouseleave(){
					removeToast('gender-toast')
				}
			},
			[
				Div('.gender-indicator', [
					Icon('.gender-icon', { icon: genderIcon }),
					Icon('.sort-icon', {
						classes: {
							'gender-sorted': demographics.gender.to(gender => gender)
						},
						icon: sortIcon,
					}),
					Icon('.regression-icon', {
						icon: regressionIcon,
						display: currentAnalysisSelections.regression.to(
							covariate => covariate == 'gender')
					}),
					Icon('.graph-icon', {
						icon: controlGraphIcon,
						display: currentAnalysisSelections.regression.to(covariate => covariate == 'gender')
					}),
				]),
				Div('.gender-summary',
					{},
					selectedDemographics.to(({ female, male, studiesWithGender, selectedStudies }) => {
						let femalePercent = Math.round((100 * female) / (female + male))
						let malePercent = Math.round((100 * male) / (female + male))
						if (studiesWithGender < Math.sqrt(selectedStudies - 1))
							return [ Div('.male-area', 'Gender') ]
						return [
							femalePercent > malePercent ? Div(
								'.female-area' + (femalePercent > 40 ? '.female-large' : ''),
								femalePercent > 40 ? femalePercent + '% Female' : 'Gender'
							) :
							Div(
								'.male-area' + (malePercent > 40 ? '.male-large' : ''),
								malePercent > 40 ? malePercent + '% Male' : 'Gender'
							),
						]
					})
				),
			]
		),
		Div('.race-control.truncate', {
			classes: {
				'active-control': demographics.race.to(Boolean),
			},
			onclick() {
				if (!isFAQDashboard){
				let selectedRace = reactive(demographics.race.valueOf())
				showRegressionForCovariate({ name: 'race', valueFromGroup: (group) => group.races, selected: demographics.race,
					characteristicSetting: [
						Div('.regression-control', [
							Label('Prefer studies for this race:'),
							SelectivityDropdown({
								placeholder: 'Race',
								allowClear: true,
								showSearchInputInDropdown: false,
								items: [
									{ text: 'White', id: 'white' },
									{ text: 'Asian', id: 'asian' },
									{ text: 'Black', id: 'black' },
									{ text: 'Native American', id: 'native american' },
									{ text: 'Pacific Islander', id: 'pacific' },
									{ text: 'Multiple Races', id: 'multi-race' },
									{ text: 'Other', id: 'other' },
								],
								value: selectedRace,
								readOnly: faqOrReadOnly.valueOf(),
							}),
						]),
					],
					onSave() {
						if (demographics.race != selectedRace.valueOf())
							demographics.race.put(selectedRace.valueOf())
					},
					characteristicAnalysis: [
						selectedDemographics.to(({ races, studiesWithRace }) => {
							if (studiesWithRace){
								let total = 0
								for (let race in races) {
									total += races[race]
								}
								let raceBars = []
								for (let race in races) {
									let percent = (races[race] / total) * 100
									let title =
										Math.round(percent) +
										'% ' +
										(raceLabels[race] || race.charAt(0).toUpperCase() + race.slice(1))
									raceBars.push(
										new Div('.race-bar.race-' + race, {
											percent,
											width: (percent > 0 ? Math.max(percent, 5) : 0) + '%',
											title,
											content: percent > 50 && race != 'unknown' ? title : '',
										})
									)
								}
								return Div('.race-graph', [sortBy(raceBars, (bar) => -bar.percent)])
							} else {
								return Div('.no-data', [Span('', 'Please select more studies with race data')])
							}
						})
					]
				})
			}
			},
			onmouseenter(event) {
				displayToast('race', event, raceMessage)
			},
			onmouseleave(){
				removeToast('race-toast')
			}
		}, [
			Div('.race-indicator', [
				Icon('.race-icon', { icon: raceIcon }),
					Icon('.sort-icon', {
						classes: {
							'race-sorted': demographics.race.to(race => race)
						},
						icon: sortIcon,
					}),
					Icon('.regression-icon', {
						icon: regressionIcon,
						display: currentAnalysisSelections.regression.to(
							covariate => covariate == 'race')
					}),
					Icon('.graph-icon', {
						icon: controlGraphIcon,
						display: currentAnalysisSelections.regression.to(covariate => covariate == 'race')
					}),
			]),
			Div(
				'.race-summary',
				{},
				selectedDemographics.to(({ races, studiesWithRace, selectedStudies }) => {
					if (studiesWithRace < Math.sqrt(selectedStudies - 1))
						return 'Race'
					let total = 0
					for (let race in races) {
						total += races[race]
					}
					/* demographics.race will return null which is falsey by default until the user selects a race from the race dropdown */
					let selectedRace = demographics.race.valueOf()
					/* if selectedRace is true than we want to return the updated value */
					if (selectedRace) {
						const selectedPercent = (races[selectedRace] / total) * 100
						const statement = Math.round(selectedPercent || 0) + '% ' + (raceLabels[selectedRace] || selectedRace.charAt(0).toUpperCase() + selectedRace.slice(1))
						return statement
					} else {
						/* else we provide the user the most common race by default */
						let mostCommonRace
						let mostCommonPercent = 0
						for (let race in races) {
							let percent = (races[race] / total) * 100
							if (percent > mostCommonPercent) {
								mostCommonPercent = percent
								mostCommonRace = race
							}
						}
						return mostCommonRace && mostCommonRace != 'unknown' ?
						 (Math.round(mostCommonPercent) + '% ' +
							(raceLabels[mostCommonRace] || mostCommonRace.charAt(0).toUpperCase() + mostCommonRace.slice(1))) :
						 'Race'
					}
				})
			),
		]),
		Div('.follow-up-control.truncate', {onmouseenter(event) {
			displayToast('follow-up', event, followUpMessage)
		},
		onmouseleave(){
			removeToast('follow-up-toast')
		}}, [
			Div('.follow-up-indicator', [Icon('.follow-up-icon',{ icon: clockIcon })]),
			// just a label for now, TODO: eventually add support for specifying a preferred follow-up time.
			Div('.follow-up-summary', {
				content: followUpDescription.to(description => description || 'Follow-Up'),
			}),
		]),
		Div('.study-participants-control.truncate', [
			Div('.study-participants-indicator', [Icon('.participants-icon', { icon: populationIcon })]),
			Div('.study-participants-count', {
				content: selectedDemographics.to((selectedDemographics) =>
					selectedDemographics.totalN ? selectedDemographics.totalN.toLocaleString() : 'Participants'
				),
			}),
		], {
			onmouseenter(event) {
				displayToast('participants', event, participantsMessage)
			},
			onmouseleave(){
				removeToast('participants-toast')
			}
		}),
	])
])

const numToString = (x) =>
	isNaN(x)
		? '?'
		: x
				.toFixed(Math.min(Math.max(-Math.floor(Math.log10(Math.abs(x))) + 1, 0), 8))
				.replace(/\.(\d*?)0+$/, (t, mantissa) => (mantissa ? '.' + mantissa : ''))

const raceLabels = {
	black: 'Black',
	american: 'Native American',
	'multi-race': 'Multiple Races',
	pacific: 'Pacific Islander',
}
